"use client";

import { useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import Link from "next/link";
import type { Category } from "@/types";
import {
  MagnifyingGlassIcon,
  XMarkIcon,
  Squares2X2Icon,
  ClockIcon,
  AdjustmentsHorizontalIcon,
} from "@heroicons/react/24/outline";
import MobileFiltersBottomSheet from "./MobileFiltersBottomSheet";

type CompaniesMobileHeaderProps = {
  allCategories: Category[];
  currentCategorySlug?: string;
  initialSearch?: string;
  neighborhoods?: string[];
};

export default function CompaniesMobileHeader({
  allCategories,
  currentCategorySlug,
  initialSearch = "",
  neighborhoods = [],
}: CompaniesMobileHeaderProps) {
  const [searchQuery, setSearchQuery] = useState(initialSearch);
  const [showFilters, setShowFilters] = useState(false);
  const router = useRouter();
  const searchParams = useSearchParams();

  const isOpenNow = searchParams.get("open_now") === "true";

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      router.push(`/empresas?search=${encodeURIComponent(searchQuery.trim())}`);
    } else {
      router.push("/empresas");
    }
  };

  const handleClearSearch = () => {
    setSearchQuery("");
    router.push("/empresas");
  };

  const toggleOpenNow = () => {
    const params = new URLSearchParams(searchParams.toString());
    if (isOpenNow) {
      params.delete("open_now");
    } else {
      params.set("open_now", "true");
    }
    params.delete("page");
    router.push(`/empresas?${params.toString()}`);
  };

  return (
    <>
      <div className="sticky top-0 z-40 bg-white border-b border-gray-200">
        {/* Header com busca */}
        <form
          onSubmit={handleSearch}
          className="px-4 py-3 flex items-center gap-3"
        >
          <div className="flex-1 relative">
            <input
              type="text"
              placeholder="Buscar comércios..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-10 py-2 bg-gray-100 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-mygreen/20"
            />
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            {searchQuery && (
              <button
                type="button"
                onClick={handleClearSearch}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            )}
          </div>
        </form>

        {/* Categorias em scroll horizontal */}
        <div className="px-4 pb-2 overflow-x-auto scrollbar-hide">
          <div className="flex gap-2">
            {/* Botão "Todos" */}
            <Link
              href="/empresas"
              className={`shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-colors flex gap-2 ${
                !currentCategorySlug
                  ? "bg-mygreen text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              <Squares2X2Icon className="w-5 h-5" />
              <span>Todos</span>
            </Link>

            {allCategories.map((category) => (
              <Link
                key={category.id}
                href={`/categoria/${category.slug}`}
                className={`shrink-0 flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  currentCategorySlug === category.slug
                    ? "bg-mygreen text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                <span>{category.name}</span>
              </Link>
            ))}
          </div>
        </div>

        {/* Segunda linha - Filtros */}
        <div className="px-4 pb-3 overflow-x-auto scrollbar-hide border-t border-gray-100">
          <div className="flex gap-2 pt-2">
            {/* Botão "Aberto Agora" */}
            <button
              onClick={toggleOpenNow}
              className={`shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-colors flex gap-2 ${
                isOpenNow
                  ? "bg-mygreen text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              <ClockIcon className="w-4 h-4" />
              <span>Aberto Agora</span>
            </button>

            {/* Botão "Mais Filtros" */}
            <button
              onClick={() => setShowFilters(true)}
              className="shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-colors flex gap-2 bg-gray-100 text-gray-700 hover:bg-gray-200"
            >
              <AdjustmentsHorizontalIcon className="w-4 h-4" />
              <span>Mais Filtros</span>
            </button>
          </div>
        </div>
      </div>

      {/* Bottom Sheet de Filtros */}
      <MobileFiltersBottomSheet
        isOpen={showFilters}
        onClose={() => setShowFilters(false)}
        neighborhoods={neighborhoods}
      />
    </>
  );
}
